/**
 * Orda OS — Realtime engine v2 (после advisor stress-review).
 *
 * Закрывает 7 проблем production realtime:
 *   1. Out-of-order events: version-based ordering guard.
 *   2. Missed events on reconnect: full re-fetch + replace state.
 *   3. Duplicate events: applyEntity idempotent.
 *   4. Race swallowing: НЕ здесь (errors.ts wrappers).
 *   5. ⚠️ DELETE without version → "ghost entities":
 *      tombstones Map<id, version_at_delete>. UPDATE с version <= tombstone
 *      ignored. На reconnect fresh entity с version <= tombstone skipped.
 *      ⚠️ Требует REPLICA IDENTITY FULL на таблицах (см. 00015 migration).
 *   6. ⚠️ Disconnect без re-subscribe → "channel умер навсегда":
 *      scheduleReconnect с exponential backoff (2/4/8/16/30 sec cap).
 *      На SUBSCRIBED reset backoff counter.
 *   7. ⚠️ Channel name special chars: hash filter for safe naming.
 *
 * Tombstone TTL: cap 1000 entries, evict oldest. Защита от unbounded grow.
 *
 * Использование:
 *   const stream = createKitchenOrdersStream(tenantId, { fetch });
 *   const unsub = stream.subscribe((state, status) => render(state, status));
 *   await stream.start();
 *   // ...
 *   await stream.stop();
 *   unsub();
 */
// Sentry: capture realtime CHANNEL_ERROR (это структурная проблема —
// RLS broken, table missing, auth invalid). Одна capture на channel per session.
// Порог и условия — в realtimeReport.ts (чистая функция под тестами).
const reportedRealtimeErrors = new Set<string>();

/** Вкладка в фоне: WS рвёт сама ОС, такой обрыв не наш баг. SSR → false. */
function isDocumentHidden(): boolean {
  return typeof document !== "undefined" && document.hidden === true;
}

// Момент последнего ухода вкладки в фон. Нужен потому, что document.hidden
// читается уже в момент прилёта CHANNEL_ERROR: мобильная ОС морозит страницу,
// попытки копятся в фоне, а колбэк доходит ПОСЛЕ возврата — вкладка снова
// видима, и один только isDocumentHidden() такой эпизод не ловит. См.
// realtimeReport.ts. Слушатель пассивный, ставится один раз на страницу и общий
// для всех стримов, поэтому снимать его на stop() не нужно.
let lastHiddenAtMs: number | null = null;
let visibilityTracked = false;

function trackVisibility(): void {
  if (visibilityTracked) return;
  if (typeof document === "undefined") return;
  visibilityTracked = true;
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) lastHiddenAtMs = Date.now();
  });
  // pagehide — страховка для iOS Safari: при свайпе «домой» visibilitychange
  // приходит не всегда, а WS к этому моменту уже мёртв.
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", () => {
      lastHiddenAtMs = Date.now();
    });
  }
}

function msSinceHidden(): number | null {
  return lastHiddenAtMs === null ? null : Date.now() - lastHiddenAtMs;
}

// Ставим сразу на импорте модуля, а не в start() стрима: важен уход в фон,
// который случился ДО падения канала, — подписаться позже значит его прозевать.
// В SSR/тестах document нет и вызов тихо ничего не делает.
trackVisibility();

function reportRealtimeError(
  channelName: string,
  status: string,
  attempts: number,
  level: Exclude<RealtimeReportVerdict, "skip">,
  metrics?: RealtimeStreamMetrics,
): void {
  const key = `${channelName}:${status}`;
  if (reportedRealtimeErrors.has(key)) return;
  reportedRealtimeErrors.add(key);
  void (async () => {
    try {
      const obs = await import("@cafe-os/observability");
      obs.captureException(
        new Error(`Realtime ${status}: ${channelName} (after ${attempts} retries)`),
        {
          realtime: "true",
          channel: channelName,
          status,
          attempts: String(attempts),
          // Видно прямо в issue, почему уровень такой: warning = эпизод
          // зацепил уход вкладки в фон, error = вкладка была на виду всё время.
          backgrounded: String(level === "warning"),
          // Gap 3: снимок метрик стрима — по нему структурную поломку видно
          // в контексте (сколько reconnect'ов, какой лаг был до падения).
          ...(metrics ?? {}),
        },
        level,
      );
    } catch { /* observability unavailable — silent noop */ }
  })();
}

import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
  SupabaseClient,
} from "@supabase/supabase-js";
import { getSupabaseClient } from "./client";
import { realtimeReportVerdict, type RealtimeReportVerdict } from "./realtimeReport";
import {
  createRealtimeMetrics,
  deliveryLagFromCommit,
  type RealtimeStreamMetrics,
} from "./realtimeMetrics";

// Gap 3 (prod observability): флап — цикл переподключений, который чинится сам
// и потому не дотягивает до порога CHANNEL_ERROR выше, но крутит re-fetch
// каждые ~2с (soak 2026-08-20: 143 витка при аптайме 95.6%). Решение «когда
// репортить» — в realtimeMetrics.ts (чистая функция под тестами), здесь только
// доставка в Sentry. warning, не error: канал жив, но деградирует.
function reportRealtimeFlap(channelName: string, metrics: RealtimeStreamMetrics): void {
  void (async () => {
    try {
      const obs = await import("@cafe-os/observability");
      obs.captureException(
        new Error(
          `Realtime flapping: ${channelName} (${metrics.reconnectsLastMinute} reconnects/min)`,
        ),
        {
          realtime: "flap",
          channel: channelName,
          ...metrics,
        },
        "warning",
      );
    } catch { /* observability unavailable — silent noop */ }
  })();
}

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export interface EntityWithVersion {
  id: string;
  version: number;
  updated_at: string;
}

export type ChannelStatus =
  | "INITIAL"
  | "FETCHING"
  | "CONNECTED"
  | "DISCONNECTED"
  | "RECONNECTING"
  | "CLOSED";

export interface RealtimeStreamConfig<T extends EntityWithVersion> {
  table: string;
  filter: string;
  fetch: () => Promise<T[]>;
  shouldKeep?: (entity: T) => boolean;
  channelName?: string;
  /**
   * Optional client override — TEST-ONLY (см. CONTRACT §9.5).
   * Если не передан — используется singleton getSupabaseClient().
   * Apps НЕ должны передавать это.
   */
  client?: SupabaseClient;
  /**
   * Bug #5b fix (option 1, owner-approved 2026-05-10): transform raw realtime
   * payload (payload.new / payload.old) BEFORE applyEvent guards.
   *
   * Why: applyEvent's `if (!incoming.id) return` guard drops payloads where
   * the row's PK column is not literally `id`. cart_lines (PK=line_id) and
   * session_guests (composite PK) silently lost all INSERT/UPDATE/DELETE
   * events, breaking CONTRACT §9 convergence for multi-guest tables.
   * fetch() callers already do this transformation (cartLineToEntity etc.);
   * this hook brings symmetry to the realtime path.
   *
   * If not provided, raw payload passes through unchanged (back-compat for
   * orders/sessions/etc. that have native `id` PK).
   */
  mapPayload?: (raw: Record<string, unknown> | null | undefined) => T | null;
}

type Subscriber<T> = (state: ReadonlyMap<string, T>, status: ChannelStatus) => void;

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

/** Stable hash for safe channel naming (no special chars in Supabase). */
function simpleHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(16);
}

/**
 * Tombstone TTL: 15 минут.
 * Обоснование: legitimate stale event (от lag/reconnect) не может опаздывать
 * больше чем на network/realtime buffer + reconnect backoff. 15 мин — щедрый
 * запас. Старше — это всё равно баг, не late-event.
 *
 * НЕ используем LRU cap (как раньше): busy cafe (200 deletes/час × 6h = 1200)
 * мог бы вытеснить нужные tombstones и получить ghost entity.
 * TTL независим от объёма — old всегда выселяется.
 */
const TOMBSTONE_TTL_MS = 15 * 60 * 1000;

/** Eviction trigger: запускаем cleanup expired tombstones когда размер > N. */
const TOMBSTONE_CLEANUP_THRESHOLD = 500;

// Exponential backoff schedule (ms): 2s, 4s, 8s, 16s, 30s cap.
// + jitter 0..500ms против thundering herd при массовом reconnect.
// Экспортируется только для чтения: soakMetrics восстанавливает по этой лестнице
// глубину серии переподключений, наблюдая поток снаружи. Логика reconnect не меняется.
export const BACKOFF_DELAYS_MS = [2000, 4000, 8000, 16000, 30000];
const BACKOFF_JITTER_MS = 500;

// ────────────────────────────────────────────────────────────────────────────
// RealtimeStream
// ────────────────────────────────────────────────────────────────────────────

interface Tombstone {
  version: number;
  at: number;  // ms epoch — для LRU eviction
}

export class RealtimeStream<T extends EntityWithVersion> {
  private state: Map<string, T> = new Map();
  /** Tombstones: id → {version, at}. Защита от ghost entities (out-of-order DELETE). */
  private tombstones: Map<string, Tombstone> = new Map();
  private status: ChannelStatus = "INITIAL";
  private channel: RealtimeChannel | null = null;
  private subscribers = new Set<Subscriber<T>>();
  private started = false;
  /** Текущая попытка reconnect (для backoff index). Reset to 0 на CONNECTED. */
  private reconnectAttempts = 0;
  // Шумоподавление: один репорт CHANNEL_ERROR за «эпизод» падения, а не каждый
  // цикл реконнекта (зависший канал иначе слал сотни одинаковых — Sentry 10).
  // Сбрасывается на SUBSCRIBED, так что новый эпизод снова отрепортится.
  private reportedChannelError = false;
  // Gap 4: id, применённые живыми событиями, пока snapshot-fetch в полёте —
  // снимок мог быть взят до их коммита, replaceState не должен их терять.
  private liveDuringFetch: Set<string> | null = null;
  // Gap 4: refresh() снимает снимок ДО join нового канала — окно снимок→join
  // добирается catch-up fetch'ем на первом SUBSCRIBED после refresh.
  private pendingCatchUp = false;
  /** Pending reconnect timeout. */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Gap 3: счётчики наблюдаемости (reconnects / staleRejected / delivery lag). */
  private metrics = createRealtimeMetrics();

  constructor(private cfg: RealtimeStreamConfig<T>) {}

  /** Возвращает client из cfg.client (test override) или singleton. */
  private client(): SupabaseClient {
    return this.cfg.client ?? getSupabaseClient();
  }

  // ── Public API ────────────────────────────────────────────────────────────

  getState(): ReadonlyMap<string, T> {
    return new Map(this.state);
  }

  getStatus(): ChannelStatus {
    return this.status;
  }

  /** Gap 3: снимок метрик наблюдаемости (счётчики поверх LOCKED-движка). */
  getMetrics(): RealtimeStreamMetrics {
    return this.metrics.getMetrics(Date.now());
  }

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    this.setStatus("FETCHING");

    try {
      const initial = await this.cfg.fetch();
      this.replaceState(initial);
    } catch (err) {
       
      console.warn(`[realtime/${this.cfg.table}] initial fetch failed:`, err);
      this.setStatus("DISCONNECTED");
      this.scheduleReconnect();
      return;
    }

    this.subscribeChannel();
  }

  /**
   * Пробуждение после фона (инцидент 2026-07-24, гости на Android+iPhone):
   * мобильная ОС убивает WebSocket при блокировке экрана / уходе в другое
   * приложение МОЛЧА — страница возвращается «живой» (React state цел), но
   * канал мёртв, а status у клиента всё ещё CONNECTED. Данные застывают
   * навсегда → для гостя это «сайт завис».
   *
   * refresh() = тот же путь, что при reconnect (snapshot-fetch + новый канал),
   * поэтому version-guard / convergence не затронуты. Канал пересоздаём
   * безусловно: доверять статусу нельзя — именно он и врёт после фона.
   * Вызывать ТОЛЬКО после существенного фона (см. attachStreamWake).
   */
  async refresh(): Promise<void> {
    if (!this.started) return;
    try {
      // Gap 4: старый канал ещё подключён и может доставлять события, пока
      // снимок в полёте — их нельзя затереть replaceState'ом.
      this.liveDuringFetch = new Set();
      const fresh = await this.cfg.fetch();
      this.replaceState(fresh);
    } catch (err) {
      console.warn(`[realtime/${this.cfg.table}] wake refresh failed:`, err);
      this.setStatus("DISCONNECTED");
      this.scheduleReconnect();
      return;
    } finally {
      this.liveDuringFetch = null;
    }
    // Отпускаем канал ДО removeChannel: его прощальный CLOSED прилетает во
    // время await и не должен считать себя владельцем (guard в subscribeChannel).
    const old = this.channel;
    this.channel = null;
    if (old) {
      try {
        await this.client().removeChannel(old);
      } catch {
        /* ignore */
      }
    }
    // Gap 4: снимок выше взят ДО join нового канала — коммиты из этого окна
    // не в снимке и не в событиях. Первый SUBSCRIBED сделает catch-up fetch.
    this.pendingCatchUp = true;
    this.subscribeChannel();
  }

  subscribe(cb: Subscriber<T>): () => void {
    this.subscribers.add(cb);
    cb(new Map(this.state), this.status);
    return () => {
      this.subscribers.delete(cb);
    };
  }

  async stop(): Promise<void> {
    this.started = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // Тот же порядок «отпустить → снять», что в refresh/recreateChannel.
    const old = this.channel;
    this.channel = null;
    if (old) {
      try {
        await this.client().removeChannel(old);
      } catch {
        /* ignore */
      }
    }
    this.setStatus("CLOSED");
    this.subscribers.clear();
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  private subscribeChannel() {
    const supabase = this.client();
    const baseName = this.cfg.channelName ?? `${this.cfg.table}:${simpleHash(this.cfg.filter)}`;
    // Same fix as AppendStream/UpsertStream: append random suffix per channel
    // instance to bypass supabase-js channel cache. React.StrictMode
    // mount→unmount→mount creates 2nd RealtimeStream before 1st channel was
    // removeChannel'd; supabase.channel(baseName) returns existing already-
    // subscribed channel → .on() throws "cannot add postgres_changes
    // callbacks after subscribe()". Random suffix per (re)subscription
    // ensures fresh channel each time.
    const channelName = `${baseName}:${Math.random().toString(36).slice(2, 10)}`;

    const ch = supabase
      .channel(channelName)
      .on(
        "postgres_changes" as never,
        {
          event: "*",
          schema: "public",
          table: this.cfg.table,
          filter: this.cfg.filter,
        },
        (payload: RealtimePostgresChangesPayload<T>) => this.applyEvent(payload),
      );
    // Владение фиксируем ДО subscribe(): guard ниже сверяется с this.channel
    // и обязан быть верен даже при синхронной доставке статуса.
    this.channel = ch;
    ch.subscribe((subscribeStatus) => {
      // Бэкпорт guard'а REACT-11 из Append/UpsertStream: канал, который мы уже
      // сами сняли (removeChannel в recreateChannel/refresh/stop), напоследок
      // стреляет CLOSED — он больше не владеет потоком и не должен гнать
      // реконнект (иначе вечный флап: soak на alem 2026-08-20, витки по ~2с).
      if (this.channel !== ch) return;
      if (subscribeStatus === "SUBSCRIBED") {
        const wasReconnecting = this.status === "RECONNECTING" || this.status === "DISCONNECTED";
        this.reconnectAttempts = 0;  // reset backoff on success
        this.reportedChannelError = false;  // новый эпизод сможет отрепортиться
        this.setStatus("CONNECTED");
        if (wasReconnecting || this.pendingCatchUp) {
          // After reconnect — re-fetch для catch-up missed events.
          // pendingCatchUp (Gap 4): после refresh() снимок был взят до join —
          // добираем коммиты из окна снимок→join тем же проверенным путём.
          this.pendingCatchUp = false;
          void this.handleReconnectFetch();
        }
      } else if (
        subscribeStatus === "CHANNEL_ERROR" ||
        subscribeStatus === "TIMED_OUT" ||
        subscribeStatus === "CLOSED"
      ) {
        // Шлём в Sentry только затяжной CHANNEL_ERROR — см. realtimeReport.ts:
        // короткие серии даёт обычная жизнь (лифт, фон, спящий ноутбук), а
        // структурная поломка не чинится и копит попытки дальше.
        const verdict = realtimeReportVerdict({
          status: subscribeStatus,
          attempts: this.reconnectAttempts,
          documentHidden: isDocumentHidden(),
          msSinceHidden: msSinceHidden(),
        });
        if (verdict !== "skip" && !this.reportedChannelError) {
          reportRealtimeError(
            channelName,
            subscribeStatus,
            this.reconnectAttempts,
            verdict,
            this.metrics.getMetrics(Date.now()),
          );
          this.reportedChannelError = true;
        }
        if (this.started) {
          this.setStatus("DISCONNECTED");
          this.scheduleReconnect();
        }
      }
    });
  }

  /**
   * Schedule reconnect with exponential backoff.
   * Idempotent: повторный вызов до сработки timer'а — no-op.
   */
  private scheduleReconnect() {
    if (!this.started) return;
    if (this.reconnectTimer) return;

    const idx = Math.min(this.reconnectAttempts, BACKOFF_DELAYS_MS.length - 1);
    const baseDelay = BACKOFF_DELAYS_MS[idx];
    // Jitter [0, 500) ms — против thundering herd когда 100 клиентов
    // одновременно reconnect'ят при WiFi recovery.
    const jitter = Math.floor(Math.random() * BACKOFF_JITTER_MS);
    const delay = baseDelay + jitter;
    this.reconnectAttempts++;

    // Gap 3: флап (>5 витков/мин) сам себя чинит и порога CHANNEL_ERROR не
    // достигает — ловим его частотой. Один репорт на эпизод (см. realtimeMetrics).
    if (this.metrics.recordReconnect(Date.now()) === "report") {
      const baseName = this.cfg.channelName ?? `${this.cfg.table}:${simpleHash(this.cfg.filter)}`;
      reportRealtimeFlap(baseName, this.metrics.getMetrics(Date.now()));
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.recreateChannel();
    }, delay);
  }

  /**
   * Tear down old channel + create new one.
   * Re-fetch state happens on SUBSCRIBED callback (handleReconnectFetch).
   */
  private async recreateChannel() {
    if (!this.started) return;
    this.setStatus("RECONNECTING");
    // Отпускаем канал ДО removeChannel — см. комментарий в refresh().
    const old = this.channel;
    this.channel = null;
    if (old) {
      try {
        await this.client().removeChannel(old);
      } catch {
        /* ignore */
      }
    }
    // Эскалация до транспорта (ночной soak на alem 2026-08-21): после жёсткого
    // обрыва WebSocket может остаться полуоткрытым «зомби» — клиент считает его
    // живым, join'ы до сервера не долетают и таймаутят часами, а пересоздание
    // КАНАЛА сокет не трогает. Прошли полный круг лестницы (5-я, 10-я, ...
    // попытка) — принудительно закрываем сокет; subscribe() ниже откроет новый.
    // Соседним каналам того же клиента это стоит одного быстрого reconnect
    // (их лестницы это уже умеют), для зомби — единственный путь наружу.
    // await обязателен: connect() — no-op, пока клиент в isDisconnecting().
    if (this.reconnectAttempts % BACKOFF_DELAYS_MS.length === 0) {
      try {
        await this.client().realtime.disconnect();
      } catch {
        /* ignore */
      }
    }
    this.subscribeChannel();
  }

  /** Re-fetch state after reconnect (tombstones-aware). */
  private async handleReconnectFetch() {
    this.setStatus("FETCHING");
    // Gap 4: подписка уже живая — события, применённые пока снимок в полёте,
    // защищаем от затирания (снимок мог быть взят до их коммита).
    this.liveDuringFetch = new Set();
    try {
      const fresh = await this.cfg.fetch();
      this.replaceState(fresh);
      this.setStatus("CONNECTED");
    } catch (err) {

      console.warn(`[realtime/${this.cfg.table}] reconnect fetch failed:`, err);
      this.setStatus("DISCONNECTED");
      this.scheduleReconnect();
    } finally {
      this.liveDuringFetch = null;
    }
  }

  // ── Event application ─────────────────────────────────────────────────────

  private applyEvent(payload: RealtimePostgresChangesPayload<T>) {
    // Gap 3: лаг доставки commit_timestamp → приём. Считаем каждое событие,
    // включая те, что дальше отбросят guard'ы.
    this.metrics.recordDelivery(
      deliveryLagFromCommit(payload.commit_timestamp, Date.now()),
    );
    if (payload.eventType === "DELETE") {
      // Bug #5b: route payload.old through mapPayload too — applyDelete
      // needs `id` and `version` to upsert tombstone correctly.
      const oldRaw = payload.old as Record<string, unknown> | null;
      const oldMapped = this.cfg.mapPayload
        ? this.cfg.mapPayload(oldRaw)
        : (oldRaw as Partial<T> | null);
      this.applyDelete(oldMapped as Partial<T> | null);
      return;
    }

    const newRaw = payload.new as Record<string, unknown> | null;
    const incoming = this.cfg.mapPayload
      ? this.cfg.mapPayload(newRaw)
      : (newRaw as T | null);
    if (!incoming || !incoming.id) return;
    if (this.applyEntity(incoming)) this.notify();
  }

  /**
   * Apply DELETE: tombstone the id at deleted version.
   * Безопасный fallback когда REPLICA IDENTITY не FULL (нет version в payload):
   *   • Если entity был в state — tombstone = last known local version.
   *     Stale UPDATE с version <= local будет blocked (correct).
   *     Legitimate новый UPDATE с version > local пройдёт (resurrect — bug
   *     на стороне backend, но мы не делаем "вечный зомби-блок").
   *   • Если entity никогда не было в local state — tombstone не ставим.
   *     Late UPDATE может всё ещё прийти и pass through (вполне допустимо,
   *     UI просто не покажет если shouldKeep вернёт false).
   *   • НЕ используем MAX_SAFE_INTEGER fallback — это блокировало бы
   *     все будущие events на этом id навсегда.
   */
  private applyDelete(oldRow: Partial<T> | null) {
    if (!oldRow?.id) return;
    const id = oldRow.id;
    let version: number;

    if (typeof oldRow.version === "number") {
      version = oldRow.version;
    } else {
      // REPLICA IDENTITY FULL не настроен на этой таблице, либо payload
      // обрезан Postgres logical replication.
      const current = this.state.get(id);
      if (!current) {
        // Никогда не видели — нет смысла tombstone'ить (нечего блокировать).
        return;
      }
      version = current.version;
       
      console.warn(
        `[realtime/${this.cfg.table}] DELETE without version field — ` +
        `REPLICA IDENTITY FULL may not be configured. Tombstoning at last known version=${version}.`,
      );
    }

    this.upsertTombstone(id, version);

    if (this.state.has(id)) {
      this.state.delete(id);
      this.notify();
    }
  }

  /**
   * Apply INSERT/UPDATE event.
   * Returns true if state changed.
   *
   * Guards (in order):
   *   1. Tombstone: incoming.version <= tombstone[id].version → ignore
   *   2. Ordering: incoming.version <= state[id].version → ignore
   *   3. shouldKeep: false → remove from state (entity вышел из view)
   *   4. else: set in state
   */
  private applyEntity(incoming: T): boolean {
    const id = incoming.id;

    // 1. Tombstone guard — защита от ghost entity (DELETE+stale UPDATE race)
    const tomb = this.tombstones.get(id);
    if (tomb && incoming.version <= tomb.version) {
      this.metrics.recordStaleRejected();  // Gap 3: version drift counter
      return false;
    }

    // 2. Ordering guard — duplicate / older event
    const current = this.state.get(id);
    if (current && incoming.version <= current.version) {
      this.metrics.recordStaleRejected();  // Gap 3: version drift counter
      return false;
    }

    // 3. shouldKeep filter
    if (this.cfg.shouldKeep && !this.cfg.shouldKeep(incoming)) {
      if (this.state.has(id)) {
        this.state.delete(id);
        return true;
      }
      return false;
    }

    this.state.set(id, incoming);
    this.liveDuringFetch?.add(id); // Gap 4: живая строка поверх летящего снимка
    return true;
  }

  /** Replace entire state from fresh fetch. Tombstone-aware. */
  private replaceState(fresh: T[]): void {
    const newState = new Map<string, T>();
    for (const e of fresh) {
      if (!e.id) continue;

      // Tombstone: skip resurrect of deleted entity
      const tomb = this.tombstones.get(e.id);
      if (tomb && e.version <= tomb.version) continue;

      if (this.cfg.shouldKeep && !this.cfg.shouldKeep(e)) continue;

      // Если local state свежее — оставляем local (защита от stale fetch)
      const local = this.state.get(e.id);
      if (local && local.version > e.version) {
        newState.set(e.id, local);
      } else {
        newState.set(e.id, e);
      }
    }
    // Gap 4: строка, применённая живым событием пока снимок был в полёте,
    // могла не попасть в снимок (коммит позже его взятия) — не теряем её.
    // Событие строго новее снимка; удаление пока-offline строк не затронуто:
    // их id в liveDuringFetch нет.
    if (this.liveDuringFetch) {
      for (const id of this.liveDuringFetch) {
        if (!newState.has(id)) {
          const local = this.state.get(id);
          if (local) newState.set(id, local);
        }
      }
    }
    this.state = newState;
    this.notify();
  }

  /**
   * Add tombstone with TTL-based eviction.
   * Cleanup триггерится когда размер превышает THRESHOLD — амортизированно
   * O(1) per upsert. Старые > 15 мин tombstones удаляются за один проход.
   */
  private upsertTombstone(id: string, version: number) {
    const existing = this.tombstones.get(id);
    if (existing && version <= existing.version) {
      return;  // already newer or equal
    }
    this.tombstones.set(id, { version, at: Date.now() });

    // Amortized cleanup
    if (this.tombstones.size > TOMBSTONE_CLEANUP_THRESHOLD) {
      this.evictExpiredTombstones();
    }
  }

  /** Удаляет tombstones старше TOMBSTONE_TTL_MS. Один проход по Map. */
  private evictExpiredTombstones() {
    const cutoff = Date.now() - TOMBSTONE_TTL_MS;
    for (const [id, t] of this.tombstones) {
      if (t.at < cutoff) {
        this.tombstones.delete(id);
      }
    }
  }

  // ── Test hooks (наружу для unit-тестов reducer-а) ─────────────────────────

  /**
   * TEST-ONLY: применить event синхронно, минуя channel/network.
   * Используется в scripts/test-realtime-*.ts для reducer unit tests
   * (out-of-order guard, idempotency). НЕ ВЫЗЫВАТЬ в app code.
   */
  __testApplyEvent(payload: RealtimePostgresChangesPayload<T>): void {
    this.applyEvent(payload);
  }

  /** TEST-ONLY: tombstone напрямую (для проверки tombstone-guard). */
  __testTombstone(id: string, version: number): void {
    this.upsertTombstone(id, version);
  }

  /** TEST-ONLY: подсадить state без fetch (быстрая инициализация теста). */
  __testSeedState(entities: T[]): void {
    for (const e of entities) {
      if (e.id) this.state.set(e.id, e);
    }
  }

  // ── Notify ────────────────────────────────────────────────────────────────

  private setStatus(s: ChannelStatus) {
    if (this.status !== s) {
      this.status = s;
      this.notify();
    }
  }

  private notify() {
    const snapshot = new Map(this.state);
    for (const cb of this.subscribers) {
      try {
        cb(snapshot, this.status);
      } catch (err) {
         
        console.warn("[realtime] subscriber threw:", err);
      }
    }
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Convenience constructors
// ────────────────────────────────────────────────────────────────────────────

export function createKitchenOrdersStream<T extends EntityWithVersion>(
  tenantId: string,
  options: {
    fetch: () => Promise<T[]>;
    shouldKeep?: (o: T) => boolean;
    /** TEST-ONLY override (см. CONTRACT §9.5). */
    client?: SupabaseClient;
    /** TEST-ONLY: уникальный suffix имени канала когда несколько subscriber'ов
     *  через РАЗНЫЕ клиенты слушают тот же tenant. Без этого Supabase
     *  websocket мультиплексирует каналы и может склеить их. */
    channelSuffix?: string;
  },
): RealtimeStream<T> {
  const baseName = `kitchen:${simpleHash(tenantId)}`;
  return new RealtimeStream<T>({
    table: "orders",
    filter: `tenant_id=eq.${tenantId}`,
    fetch: options.fetch,
    shouldKeep: options.shouldKeep,
    channelName: options.channelSuffix ? `${baseName}:${options.channelSuffix}` : baseName,
    client: options.client,
  });
}

export function createSessionOrdersStream<T extends EntityWithVersion>(
  sessionId: string,
  options: { fetch: () => Promise<T[]>; client?: SupabaseClient; channelSuffix?: string },
): RealtimeStream<T> {
  const baseName = `session-orders:${simpleHash(sessionId)}`;
  return new RealtimeStream<T>({
    table: "orders",
    filter: `session_id=eq.${sessionId}`,
    fetch: options.fetch,
    channelName: options.channelSuffix ? `${baseName}:${options.channelSuffix}` : baseName,
    client: options.client,
  });
}

export function createCartLinesStream<T extends EntityWithVersion>(
  sessionId: string,
  options: {
    fetch: () => Promise<T[]>;
    client?: SupabaseClient;
    channelSuffix?: string;
    /** Bug #5b: cart_lines PK = line_id, не id. Transform нужен для applyEvent. */
    mapPayload?: (raw: Record<string, unknown> | null | undefined) => T | null;
  },
): RealtimeStream<T> {
  const baseName = `cart:${simpleHash(sessionId)}`;
  return new RealtimeStream<T>({
    table: "cart_lines",
    filter: `session_id=eq.${sessionId}`,
    fetch: options.fetch,
    channelName: options.channelSuffix ? `${baseName}:${options.channelSuffix}` : baseName,
    client: options.client,
    mapPayload: options.mapPayload,
  });
}

// ────────────────────────────────────────────────────────────────────────────
// AppendStream — Phase 7.1 (ChatGPT advisor consensus 2026-05-05)
//
// Для append-only entities (payments, refunds, audit logs) version-based
// ordering абстракции RealtimeStream — overkill. У них нет UPDATE race,
// нет tombstones, нет version-merging. Ordering тривиален: по timestamp+id.
//
// subscribeAppend — lightweight subscriber с тем же lifecycle что RealtimeStream:
//   • singleton getSupabaseClient()
//   • channel naming через simpleHash (safe special chars)
//   • exponential backoff (BACKOFF_DELAYS_MS, BACKOFF_JITTER_MS) на reconnect
//   • CONNECTED/DISCONNECTED/RECONNECTING/CLOSED status
//   • teardown через returned unsubscribe function
//
// Invariants (ChatGPT обязательные):
//   1. Same lifecycle as RealtimeStream (parity guaranteed by re-using helpers)
//   2. Deterministic ordering: sort by (orderBy, id) — НЕ доверяем порядку
//      доставки realtime
//   3. Idempotent apply: dedupe by id (Set) — INSERT может прийти 2× при
//      reconnect/replay
//
// Для UPDATE-able entities используй RealtimeStream<EntityWithVersion>.
// ────────────────────────────────────────────────────────────────────────────

export interface AppendEntity {
  id: string;
}

export interface AppendStreamConfig<T extends AppendEntity> {
  table: string;
  filter: string;
  fetch: () => Promise<T[]>;
  /** Field used for sort ordering (e.g., "received_at", "created_at"). */
  orderBy: keyof T & string;
  /** "asc" — oldest first (chronological feed). "desc" — newest first. */
  orderDirection?: "asc" | "desc";
  channelName?: string;
  /** Test-only client override — apps НЕ передают (см. CONTRACT §9.5). */
  client?: SupabaseClient;
}

export type AppendSubscriber<T> = (state: ReadonlyArray<T>, status: ChannelStatus) => void;

export interface AppendStream<T extends AppendEntity> {
  /** Subscribe to state+status updates. Returns unsubscribe function. */
  subscribe(cb: AppendSubscriber<T>): () => void;
  /** Start fetch + realtime channel. Idempotent. */
  start(): Promise<void>;
  /** Stop channel + clear state. Idempotent. */
  stop(): Promise<void>;
  /** Пробуждение после фона — см. RealtimeStream.refresh (инцидент 2026-07-24). */
  refresh(): Promise<void>;
  /** Current snapshot (read-only). */
  getState(): ReadonlyArray<T>;
  /** Gap 3: снимок метрик наблюдаемости — паритет с RealtimeStream.getMetrics. */
  getMetrics(): RealtimeStreamMetrics;
}

/**
 * Create an append-only realtime stream.
 *
 * Lifecycle: start() → fetch all (status=FETCHING) → subscribe channel
 * (status=CONNECTED on SUBSCRIBED) → INSERT events appended + sorted +
 * deduped → stop() / unsubscribe.
 *
 * On reconnect: full re-fetch (replaces state) — гарантирует согласованность
 * с DB после long disconnect.
 */
export function createAppendStream<T extends AppendEntity>(
  config: AppendStreamConfig<T>,
): AppendStream<T> {
  const direction = config.orderDirection ?? "asc";
  const orderBy = config.orderBy;
  // Channel name MUST be unique per stream instance. supabase-js caches channels
  // by name; React.StrictMode (mount→unmount→mount) или HMR создают второй
  // stream до того как первый успел removeChannel — supabase.channel(name)
  // возвращает existing already-subscribed channel → .on() после .subscribe()
  // → "cannot add postgres_changes callbacks after subscribe()". Фикс: добавляем
  // случайный suffix per instance, кэш всегда возвращает свежий channel.
  const instanceSuffix = Math.random().toString(36).slice(2, 10);
  const channelName = config.channelName ?? `append:${config.table}:${simpleHash(config.filter)}:${instanceSuffix}`;

  // Internal state — не exposed напрямую (только через getState / subscribers).
  const byId = new Map<string, T>();
  const subscribers = new Set<AppendSubscriber<T>>();
  let status: ChannelStatus = "INITIAL";
  let channel: RealtimeChannel | null = null;
  let started = false;
  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // Шумоподавление: один репорт CHANNEL_ERROR за эпизод, сброс при SUBSCRIBED (Sentry 10).
  let reportedChannelError = false;
  // Sentry REACT-11: два конкурентных doStart (реконнект-таймер + CLOSED-колбэк
  // канала, снесённого removeChannel) интерливятся на await fetchAndReplace —
  // оба доходят до supabase.channel(имя), второй получает из кэша УЖЕ
  // подписанный инстанс первого → ch.on() бросает «cannot add postgres_changes
  // callbacks after subscribe()». Generation-счётчик: канал создаёт только
  // самый свежий doStart, устаревшие прерываются после каждого await.
  let startGen = 0;
  // Gap 3: счётчики наблюдаемости — паритет с RealtimeStream.
  const metrics = createRealtimeMetrics();

  function snapshot(): ReadonlyArray<T> {
    const arr = Array.from(byId.values());
    arr.sort((a, b) => {
      const av = a[orderBy] as unknown;
      const bv = b[orderBy] as unknown;
      if (av === bv) {
        // Tie-break by id для детерминированного порядка.
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
      }
      // Сравниваем как строки (works for ISO timestamps + numbers stringified).
      const cmp = String(av) < String(bv) ? -1 : 1;
      return direction === "asc" ? cmp : -cmp;
    });
    return arr;
  }

  function notify(): void {
    const state = snapshot();
    for (const cb of subscribers) cb(state, status);
  }

  function setStatus(next: ChannelStatus): void {
    if (status === next) return;
    status = next;
    notify();
  }

  function applyEntity(entity: T): void {
    // Dedupe by id — INSERT может прийти повторно (reconnect replay).
    if (byId.has(entity.id)) {
      // Тот же id — игнорируем (append-only, не UPDATE'ится).
      metrics.recordStaleRejected();  // Gap 3: duplicate/replay counter
      return;
    }
    byId.set(entity.id, entity);
    notify();
  }

  function applyAll(entities: T[]): void {
    byId.clear();
    for (const e of entities) byId.set(e.id, e);
    notify();
  }

  async function fetchAndReplace(): Promise<void> {
    setStatus("FETCHING");
    try {
      const data = await config.fetch();
      applyAll(data);
    } catch (err) {
       
      console.error(`[appendStream:${config.table}] fetch failed:`, err);
      setStatus("DISCONNECTED");
      scheduleReconnect();
      throw err;
    }
  }

  function scheduleReconnect(): void {
    if (!started) return;
    if (reconnectTimer) return;
    const idx = Math.min(reconnectAttempts, BACKOFF_DELAYS_MS.length - 1);
    const delay = BACKOFF_DELAYS_MS[idx] + Math.floor(Math.random() * BACKOFF_JITTER_MS);
    reconnectAttempts++;
    // Gap 3: флап-детект — см. комментарий в RealtimeStream.scheduleReconnect.
    if (metrics.recordReconnect(Date.now()) === "report") {
      reportRealtimeFlap(channelName, metrics.getMetrics(Date.now()));
    }
    setStatus("RECONNECTING");
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void doStart();
    }, delay);
  }

  async function doStart(): Promise<void> {
    if (!started) return;
    // REACT-11: этот вызов владеет поколением gen; если за время любого await
    // стартовал более новый doStart — текущий устарел и обязан выйти, не
    // создавая канал (иначе supabase.channel(имя) вернёт уже подписанный
    // инстанс свежего вызова и .on() бросит unhandled rejection).
    const gen = ++startGen;
    const supabase = config.client ?? getSupabaseClient();

    // Удалить старый channel перед re-subscribe (чтобы не было zombie).
    // Отпускаем канал ДО removeChannel (фикс ec155730, как в RealtimeStream):
    // CLOSED снятому каналу прилетает синхронно ВНУТРИ await, и пока `channel`
    // указывает на него, guard `channel !== ch` в колбэке его пропускает —
    // чужой CLOSED заводил лишний reconnect-таймер, виток рождал следующий.
    if (channel) {
      const closing = channel;
      channel = null;
      try { await supabase.removeChannel(closing); } catch { /* ignore */ }
    }
    if (!started || gen !== startGen) return;

    try {
      await fetchAndReplace();
    } catch {
      // fetchAndReplace уже schedule'ит reconnect.
      return;
    }
    if (!started || gen !== startGen) return;

    // Эскалация до транспорта (фикс 1013146d, как в RealtimeStream): после
    // жёсткого обрыва сокет может остаться полуоткрытым «зомби» — join'ы
    // таймаутят часами, а пересоздание КАНАЛА сокет не трогает. Полный круг
    // лестницы (5-я, 10-я, … попытка) — принудительно закрываем сокет;
    // subscribe() ниже откроет новый. reconnectAttempts > 0 обязателен:
    // doStart — общий путь start()/refresh(), первый заход не повод рвать
    // сокет соседям. await обязателен: connect() — no-op при isDisconnecting().
    if (reconnectAttempts > 0 && reconnectAttempts % BACKOFF_DELAYS_MS.length === 0) {
      try { await supabase.realtime.disconnect(); } catch { /* ignore */ }
      if (!started || gen !== startGen) return;
    }

    // Уникальный топик на каждый (ре)коннект (как RealtimeStream.subscribeChannel
    // и stopList.ts). channelName с instanceSuffix защищает лишь от коллизий
    // РАЗНЫХ инстансов (StrictMode/HMR); при reconnect ТОГО ЖЕ инстанса
    // supabase.channel(имя) может вернуть ещё-не-выгруженный подписанный канал →
    // ch.on() бросит "cannot add postgres_changes after subscribe()".
    const ch = supabase.channel(`${channelName}:r${Math.random().toString(36).slice(2, 8)}`);
    ch.on(
      "postgres_changes" as never,
      { event: "INSERT", schema: "public", table: config.table, filter: config.filter },
      (payload: RealtimePostgresChangesPayload<T>) => {
        // Gap 3: лаг доставки — каждое принятое событие, включая дубликаты.
        metrics.recordDelivery(deliveryLagFromCommit(payload.commit_timestamp, Date.now()));
        if (payload.eventType === "INSERT" && payload.new) {
          applyEntity(payload.new as T);
        }
      },
    );
    ch.subscribe((subStatus) => {
      // Колбэк канала, который мы уже сами сняли (removeChannel в новом
      // doStart/stop), стреляет CLOSED — он не должен гнать лишний реконнект.
      if (channel !== ch) return;
      if (subStatus === "SUBSCRIBED") {
        reconnectAttempts = 0;
        reportedChannelError = false;
        setStatus("CONNECTED");
      } else if (subStatus === "CHANNEL_ERROR" || subStatus === "TIMED_OUT" || subStatus === "CLOSED") {
        const verdict = realtimeReportVerdict({
          status: subStatus,
          attempts: reconnectAttempts,
          documentHidden: isDocumentHidden(),
          msSinceHidden: msSinceHidden(),
        });
        if (verdict !== "skip" && !reportedChannelError) {
          reportRealtimeError(
            channelName,
            subStatus,
            reconnectAttempts,
            verdict,
            metrics.getMetrics(Date.now()),
          );
          reportedChannelError = true;
        }
        setStatus("DISCONNECTED");
        scheduleReconnect();
      }
    });
    channel = ch;
  }

  return {
    subscribe(cb) {
      subscribers.add(cb);
      // Push current state immediately (sync UI on subscribe).
      cb(snapshot(), status);
      return () => {
        subscribers.delete(cb);
      };
    },
    getMetrics() {
      return metrics.getMetrics(Date.now());
    },
    async start() {
      if (started) return;
      started = true;
      reconnectAttempts = 0;
      await doStart();
    },
    /**
     * Пробуждение после фона (2026-07-24). doStart() делает ровно нужное:
     * снимает старый канал → перечитывает снапшот → подписывает новый канал.
     * Тот же путь, что reconnect, поэтому ordering/convergence не затронуты.
     * Вызывать только после существенного фона (см. attachStreamWake).
     */
    async refresh() {
      if (!started) return;
      await doStart();
    },
    async stop() {
      if (!started) return;
      started = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      const supabase = config.client ?? getSupabaseClient();
      if (channel) {
        // Отпустить до removeChannel — см. комментарий в doStart (ec155730).
        const closing = channel;
        channel = null;
        try { await supabase.removeChannel(closing); } catch { /* ignore */ }
      }
      byId.clear();
      setStatus("CLOSED");
    },
    getState() {
      return snapshot();
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────
// UpsertStream — Phase 7.3 (sessions, любой entity с UPDATE-able row).
//
// Sessions имеют denormalized columns total/paid_total/status, atomically
// updated в add_payment/place_order RPCs. realtime UPDATE event приходит на
// каждый mutation. Append-only stream (только INSERT) недостаточен — пропустит
// UPDATE'ы.
//
// UpsertStream:
//   • Handles INSERT + UPDATE events.
//   • Dedupe + replace-by-id (Map).
//   • НЕ обрабатывает DELETE (для текущих cases — sessions не удаляются,
//     status='closed' через UPDATE). При необходимости — отдельный extension.
//   • Same lifecycle parity как RealtimeStream/AppendStream: singleton client,
//     simpleHash channel naming, exponential backoff, deterministic ordering.
// ────────────────────────────────────────────────────────────────────────────

export interface UpsertEntity {
  id: string;
}

export interface UpsertStreamConfig<T extends UpsertEntity> {
  table: string;
  filter: string;
  fetch: () => Promise<T[]>;
  /** Field used for sort ordering (e.g., "created_at"). */
  orderBy: keyof T & string;
  orderDirection?: "asc" | "desc";
  channelName?: string;
  client?: SupabaseClient;
  /**
   * Bug #5c (sibling of Bug #5b): same mapPayload hook as RealtimeStreamConfig.
   * UpsertStream's applyEntity uses entity.id as Map key. For session_guests
   * (composite PK (session_id, uid)) raw payload has no id column → all events
   * clobber the same `undefined` slot. mapPayload synthesizes id from uid.
   */
  mapPayload?: (raw: Record<string, unknown> | null | undefined) => T | null;
  /**
   * M5 (аудит 2026-07-04 kitchen): опциональный version-ordering guard (как у
   * RealtimeStream). Если задан — входящий INSERT/UPDATE, чья версия по этому
   * полю <= уже сохранённой, ИГНОРИРУЕТСЯ. Без него applyEntity как раньше
   * безусловно перезаписывает (для потоков без версии: sessions/guests/…).
   * Нужен для subscribeSessionOrderLines: при reorder WS-событий устаревший
   * UPDATE откатывал блюдо (Готово→Принято) у гостя до полного refetch.
   */
  versionField?: keyof T & string;
}

/**
 * M5: решение version-ordering для createUpsertStream (чистая функция — тест).
 * false → входящий upsert устаревший/дубликат, дропаем. Нет versionField, нет
 * текущей записи, или поля не числовые → true (прежнее безусловное поведение).
 */
export function upsertVersionAllows<T extends UpsertEntity>(
  current: T | undefined,
  incoming: T,
  versionField?: (keyof T & string) | undefined,
): boolean {
  if (!versionField || !current) return true;
  const iv = incoming[versionField];
  const cv = current[versionField];
  if (typeof iv === "number" && typeof cv === "number") return iv > cv;
  return true;
}

export type UpsertSubscriber<T> = (state: ReadonlyArray<T>, status: ChannelStatus) => void;

export interface UpsertStream<T extends UpsertEntity> {
  subscribe(cb: UpsertSubscriber<T>): () => void;
  start(): Promise<void>;
  stop(): Promise<void>;
  /** Пробуждение после фона — см. RealtimeStream.refresh (инцидент 2026-07-24). */
  refresh(): Promise<void>;
  getState(): ReadonlyArray<T>;
  /** Gap 3: снимок метрик наблюдаемости — паритет с RealtimeStream.getMetrics. */
  getMetrics(): RealtimeStreamMetrics;
}

export function createUpsertStream<T extends UpsertEntity>(
  config: UpsertStreamConfig<T>,
): UpsertStream<T> {
  const direction = config.orderDirection ?? "asc";
  const orderBy = config.orderBy;
  // См. comment в createAppendStream — same StrictMode/HMR channel cache issue.
  const instanceSuffix = Math.random().toString(36).slice(2, 10);
  const channelName = config.channelName ?? `upsert:${config.table}:${simpleHash(config.filter)}:${instanceSuffix}`;

  const byId = new Map<string, T>();
  const subscribers = new Set<UpsertSubscriber<T>>();
  let status: ChannelStatus = "INITIAL";
  let channel: RealtimeChannel | null = null;
  let started = false;
  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // Шумоподавление: один репорт CHANNEL_ERROR за эпизод, сброс при SUBSCRIBED (Sentry 10).
  let reportedChannelError = false;
  // REACT-11: см. комментарий у startGen в createAppendStream — тот же
  // generation-guard против гонки конкурентных doStart.
  let startGen = 0;
  // Gap 3: метрики наблюдаемости (см. realtimeMetrics.ts).
  const metrics = createRealtimeMetrics();

  function snapshot(): ReadonlyArray<T> {
    const arr = Array.from(byId.values());
    arr.sort((a, b) => {
      const av = a[orderBy] as unknown;
      const bv = b[orderBy] as unknown;
      if (av === bv) return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
      const cmp = String(av) < String(bv) ? -1 : 1;
      return direction === "asc" ? cmp : -cmp;
    });
    return arr;
  }

  function notify(): void {
    const state = snapshot();
    for (const cb of subscribers) cb(state, status);
  }

  function setStatus(next: ChannelStatus): void {
    if (status === next) return;
    status = next;
    notify();
  }

  function applyEntity(entity: T): void {
    // M5: version-ordering guard (opt-in via config.versionField). Дропаем
    // устаревший/дубликатный upsert — иначе reorder WS откатывал бы статус.
    if (!upsertVersionAllows(byId.get(entity.id), entity, config.versionField)) {
      metrics.recordStaleRejected();  // Gap 3: version drift counter
      return;
    }
    // Upsert: дубликат по id заменяет existing (UPDATE) или вставляет (INSERT).
    byId.set(entity.id, entity);
    notify();
  }

  function applyDelete(id: string): void {
    // DELETE: убираем запись. Без этого удалённые сущности (session_guests при
    // claim/уходе/чистке дублей, discounts, expenses, service_requests…) висели
    // в UI как «фантомы» до полного refetch — а refetch бывает только при
    // reconnect. На стабильном long-lived канале (открытая вкладка часами)
    // reconnect не наступает → дубликат гостя оставался навсегда (баг 2026-06-17:
    // на ПК «За столом · 5» с двумя Ereke123, в БД и на телефоне — 4).
    if (byId.delete(id)) notify();
  }

  function applyAll(entities: T[]): void {
    byId.clear();
    for (const e of entities) byId.set(e.id, e);
    notify();
  }

  async function fetchAndReplace(): Promise<void> {
    setStatus("FETCHING");
    try {
      const data = await config.fetch();
      applyAll(data);
    } catch (err) {
       
      console.error(`[upsertStream:${config.table}] fetch failed:`, err);
      setStatus("DISCONNECTED");
      scheduleReconnect();
      throw err;
    }
  }

  function scheduleReconnect(): void {
    if (!started) return;
    if (reconnectTimer) return;
    const idx = Math.min(reconnectAttempts, BACKOFF_DELAYS_MS.length - 1);
    const delay = BACKOFF_DELAYS_MS[idx] + Math.floor(Math.random() * BACKOFF_JITTER_MS);
    reconnectAttempts++;
    // Gap 3: флап-детект — см. комментарий в RealtimeStream.scheduleReconnect.
    if (metrics.recordReconnect(Date.now()) === "report") {
      reportRealtimeFlap(channelName, metrics.getMetrics(Date.now()));
    }
    setStatus("RECONNECTING");
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void doStart();
    }, delay);
  }

  async function doStart(): Promise<void> {
    if (!started) return;
    // REACT-11: только самый свежий doStart доходит до создания канала —
    // устаревшие выходят после каждого await (см. createAppendStream).
    const gen = ++startGen;
    const supabase = config.client ?? getSupabaseClient();

    // Отпускаем канал ДО removeChannel (фикс ec155730): его синхронный CLOSED
    // проходил guard `channel !== ch` и заводил вечный флап — см. createAppendStream.
    if (channel) {
      const closing = channel;
      channel = null;
      try { await supabase.removeChannel(closing); } catch { /* ignore */ }
    }
    if (!started || gen !== startGen) return;

    try {
      await fetchAndReplace();
    } catch {
      return;
    }
    if (!started || gen !== startGen) return;

    // Эскалация до транспорта на полном круге лестницы (фикс 1013146d) —
    // механика и условия в комментарии createAppendStream.doStart.
    if (reconnectAttempts > 0 && reconnectAttempts % BACKOFF_DELAYS_MS.length === 0) {
      try { await supabase.realtime.disconnect(); } catch { /* ignore */ }
      if (!started || gen !== startGen) return;
    }

    // Уникальный топик на каждый (ре)коннект (как RealtimeStream.subscribeChannel
    // и stopList.ts) — иначе при reconnect supabase.channel(имя) может вернуть
    // ещё-не-выгруженный подписанный канал → ch.on() бросит "cannot add
    // postgres_changes after subscribe()". instanceSuffix остаётся для
    // разделения разных инстансов (StrictMode/HMR).
    const ch = supabase.channel(`${channelName}:r${Math.random().toString(36).slice(2, 8)}`);
    // Слушаем INSERT + UPDATE + DELETE. DELETE нужен для удаляемых сущностей
    // (session_guests, discounts, expenses, service_requests…); для неудаляемых
    // (sessions/tables) событие просто не приходит — handler безвреден.
    // Bug #5c: route raw payload через mapPayload если задан, иначе fall through.
    function mapNew(payload: RealtimePostgresChangesPayload<T>): T | null {
      const raw = payload.new as Record<string, unknown> | null;
      if (config.mapPayload) return config.mapPayload(raw);
      return raw as T | null;
    }
    // DELETE отдаёт старую строку в payload.old (REPLICA IDENTITY FULL у этих
    // таблиц → все колонки, включая uid для composite-PK session_guests).
    function mapOld(payload: RealtimePostgresChangesPayload<T>): T | null {
      const raw = payload.old as Record<string, unknown> | null;
      if (config.mapPayload) return config.mapPayload(raw);
      return raw as T | null;
    }
    ch.on(
      "postgres_changes" as never,
      { event: "INSERT", schema: "public", table: config.table, filter: config.filter },
      (payload: RealtimePostgresChangesPayload<T>) => {
        // Gap 3: лаг доставки — каждое принятое событие.
        metrics.recordDelivery(deliveryLagFromCommit(payload.commit_timestamp, Date.now()));
        if (payload.eventType === "INSERT") {
          const e = mapNew(payload);
          if (e && e.id) applyEntity(e);
        }
      },
    );
    ch.on(
      "postgres_changes" as never,
      { event: "UPDATE", schema: "public", table: config.table, filter: config.filter },
      (payload: RealtimePostgresChangesPayload<T>) => {
        metrics.recordDelivery(deliveryLagFromCommit(payload.commit_timestamp, Date.now()));
        if (payload.eventType === "UPDATE") {
          const e = mapNew(payload);
          if (e && e.id) applyEntity(e);
        }
      },
    );
    ch.on(
      "postgres_changes" as never,
      { event: "DELETE", schema: "public", table: config.table, filter: config.filter },
      (payload: RealtimePostgresChangesPayload<T>) => {
        metrics.recordDelivery(deliveryLagFromCommit(payload.commit_timestamp, Date.now()));
        if (payload.eventType === "DELETE") {
          const e = mapOld(payload);
          if (e && e.id) applyDelete(e.id);
        }
      },
    );
    ch.subscribe((subStatus) => {
      // CLOSED снятого нами же канала не должен гнать лишний реконнект.
      if (channel !== ch) return;
      if (subStatus === "SUBSCRIBED") {
        reconnectAttempts = 0;
        reportedChannelError = false;
        setStatus("CONNECTED");
      } else if (subStatus === "CHANNEL_ERROR" || subStatus === "TIMED_OUT" || subStatus === "CLOSED") {
        const verdict = realtimeReportVerdict({
          status: subStatus,
          attempts: reconnectAttempts,
          documentHidden: isDocumentHidden(),
          msSinceHidden: msSinceHidden(),
        });
        if (verdict !== "skip" && !reportedChannelError) {
          reportRealtimeError(
            channelName,
            subStatus,
            reconnectAttempts,
            verdict,
            metrics.getMetrics(Date.now()),
          );
          reportedChannelError = true;
        }
        setStatus("DISCONNECTED");
        scheduleReconnect();
      }
    });
    channel = ch;
  }

  return {
    subscribe(cb) {
      subscribers.add(cb);
      cb(snapshot(), status);
      return () => { subscribers.delete(cb); };
    },
    getMetrics() {
      return metrics.getMetrics(Date.now());
    },
    async start() {
      if (started) return;
      started = true;
      reconnectAttempts = 0;
      await doStart();
    },
    /**
     * Пробуждение после фона (2026-07-24). doStart() делает ровно нужное:
     * снимает старый канал → перечитывает снапшот → подписывает новый канал.
     * Тот же путь, что reconnect, поэтому ordering/convergence не затронуты.
     * Вызывать только после существенного фона (см. attachStreamWake).
     */
    async refresh() {
      if (!started) return;
      await doStart();
    },
    async stop() {
      if (!started) return;
      started = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      const supabase = config.client ?? getSupabaseClient();
      if (channel) {
        // Отпустить до removeChannel — см. комментарий в doStart (ec155730).
        const closing = channel;
        channel = null;
        try { await supabase.removeChannel(closing); } catch { /* ignore */ }
      }
      byId.clear();
      setStatus("CLOSED");
    },
    getState() {
      return snapshot();
    },
  };
}
